import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './Binders.css';
import Tabs from '../../common/Tabs/Tabs';
import BinderBrowser from './BinderBrowser';
import { entryPrice, formatMoney } from './binderMath';
import { useUser } from '../../auth/AuthPage/UserContext';
import LoginGate from '../../common/LoginGate/LoginGate';

/**
 * THE BINDER DASHBOARD - collections get real estate.
 *
 * Same language as the rest of the site: the Trades-style header
 * (page-section-head + a one-number dash-summary) and the Search-style
 * body (sticky search box, left rail of always-visible pill zones,
 * chips + sort above the grid).
 *
 * ONE box, two scopes, one pipeline: typing filters YOUR cards; when
 * nothing in the binder matches, "Search the catalog" flips the grid
 * to live catalog results (debounced, updating as you type, standard
 * +Have/+Want quick-add cards). The rail stays through both scopes -
 * its zones and counts are derived from whichever list is showing
 * (copies of your cards, or printings among the results), so the
 * pills always filter what's on screen. Clearing the name - box,
 * chip x, Clear all, or Reset - reverts to the standard binder view,
 * and the binder refreshes so fresh adds appear.
 *
 * URL contract: ?tab=wishlist (navbar/homepage deep links). Stats are
 * computed client-side from data already fetched - no new endpoints.
 * Prices are finish-accurate via min_prices (see binderMath.js).
 */

function Binders() {
    const { user } = useUser();
    const navigate = useNavigate();

    // ---------- URL state: ?tab=wishlist ----------
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') === 'wishlist' ? 'wishlist' : 'collection';
    const setActiveTab = (tab) => setSearchParams(tab === 'wishlist' ? { tab: 'wishlist' } : {});

    // ---------- data (both tabs fetched once, cached) ----------
    const [cardsCache, setCardsCache] = useState({ collection: null, wishlist: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchTabData = async (tab) => {
        if (!user) return [];
        const endpoint = tab === 'collection' ? '/api/haves' : '/api/wants';
        const res = await fetch(endpoint, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to fetch your cards.');
        const data = await res.json();
        const list = tab === 'collection' ? data.haves : data.wants;
        if (!list || list.length === 0) return [];

        const entries = list.map(row => ({ uuid: row.uuid, setcode: row.setcode }));
        const cardRes = await fetch('/api/cards/by-uuids-with-setcodes', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ entries })
        });
        if (!cardRes.ok) throw new Error('Failed to fetch card details.');
        const cardData = await cardRes.json();
        return list.map(entry => {
            const card = cardData.cards.find(cardObj => cardObj.uuid === entry.uuid);
            return { card, binder: entry };
        });
    };

    const refreshBothTabs = useCallback((withSpinner) => {
        if (!user) return;
        if (withSpinner) { setLoading(true); setError(null); }
        Promise.all([fetchTabData('collection'), fetchTabData('wishlist')])
            .then(([collectionData, wishlistData]) => {
                setCardsCache({ collection: collectionData, wishlist: wishlistData });
            })
            .catch(err => {
                if (withSpinner) {
                    setError(err.message || 'Failed to load your cards.');
                    setCardsCache({ collection: [], wishlist: [] });
                } else {
                    console.error('Error refreshing binder data:', err);
                }
            })
            .finally(() => { if (withSpinner) setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => { refreshBothTabs(true); }, [refreshBothTabs]);
    // Leaving catalog mode: pull the binder again so fresh adds appear
    const refreshQuietly = useCallback(() => refreshBothTabs(false), [refreshBothTabs]);

    const loaded = cardsCache[activeTab] !== null;

    // Active tab's entries with their finish-accurate price attached once
    // (price arrays may need JSON parsing - never do that inside a sort)
    const cards = useMemo(() => {
        return (cardsCache[activeTab] || [])
            .filter(({ card }) => card)
            .map(item => ({ ...item, _price: entryPrice(item.card, item.binder?.finish) }));
    }, [cardsCache, activeTab]);

    // ---------- edits (quantity / remove), keyed by uuid+finish ----------
    const handleUpdateEntry = (uuid, finish, newQuantity) => {
        setCardsCache(prev => ({
            ...prev,
            [activeTab]: prev[activeTab]?.map(({ card, binder }) =>
                card && card.uuid === uuid && binder.finish === finish
                    ? { card, binder: { ...binder, quantity: newQuantity } }
                    : { card, binder }
            )
        }));
    };
    const handleDeleteEntry = (uuid, finish) => {
        setCardsCache(prev => ({
            ...prev,
            [activeTab]: prev[activeTab]?.filter(({ card, binder }) =>
                !(card && card.uuid === uuid && binder.finish === finish)
            )
        }));
    };

    // Binder-only stats for the header (the summary describes YOUR
    // binder even while catalog results are on screen)
    const binderStats = useMemo(() => {
        let copies = 0, value = 0, unpriced = 0;
        cards.forEach(({ binder, _price }) => {
            const qty = binder?.quantity || 1;
            copies += qty;
            if (_price != null) value += _price * qty; else unpriced += qty;
        });
        return { copies, value, unpriced };
    }, [cards]);

    const tabCopies = (list) => (list
        ? list.reduce((n, { card, binder }) => (card ? n + (binder?.quantity || 1) : n), 0)
        : null);
    const collectionCopies = tabCopies(cardsCache.collection);
    const wishlistCopies = tabCopies(cardsCache.wishlist);

    // ---------- render ----------
    if (!user) {
        return <LoginGate title="Binders" message="Log in to manage your binder and wishlist." />;
    }

    const entryType = activeTab === 'collection' ? 'have' : 'want';
    const binderNoun = activeTab === 'collection' ? 'collection' : 'wishlist';

    return (
        <div className="bn-page">
            <header className="page-section-head binders-head">
                <h1>Binders <span className="page-section-current">{activeTab === 'collection' ? 'My Collection' : 'My Wishlist'}</span></h1>
                <Tabs
                    variant="primary"
                    active={activeTab}
                    onChange={setActiveTab}
                    items={[
                        { id: 'collection', label: `Collection${collectionCopies != null ? ` (${collectionCopies})` : ''}`, shortLabel: 'Collection' },
                        { id: 'wishlist', label: `Wishlist${wishlistCopies != null ? ` (${wishlistCopies})` : ''}`, shortLabel: 'Wishlist' },
                    ]}
                />
                {loaded && !loading && !error && binderStats.copies > 0 && (
                    <p
                        className="dash-summary"
                        title={`Lowest listed prices${binderStats.unpriced ? `; ${binderStats.unpriced} cop${binderStats.unpriced === 1 ? 'y has' : 'ies have'} no price data` : ''}`}
                    >
                        {activeTab === 'collection'
                            ? `Est. value ${formatMoney(binderStats.value)}`
                            : `Est. ${formatMoney(binderStats.value)} to complete`}
                    </p>
                )}
            </header>

            {loading ? (
                <div className="binders-placeholder">Loading your cards...</div>
            ) : error ? (
                <div className="binders-placeholder" style={{ color: 'var(--danger)' }}>{error}</div>
            ) : cards.length === 0 ? (
                /* -------- empty binder: Search is where cards come from -------- */
                <div className="dash-sec-empty bn-empty">
                    <p>You haven't added any cards to your {binderNoun} yet.</p>
                    <button className="cta-gold" onClick={() => navigate('/search')}>Find cards to add</button>
                </div>
            ) : (
                <BinderBrowser
                    items={cardsCache[activeTab]}
                    entryType={entryType}
                    editable
                    onUpdateEntry={handleUpdateEntry}
                    onDeleteEntry={handleDeleteEntry}
                    placeholder={activeTab === 'collection' ? 'Search your collection…' : 'Search your wishlist…'}
                    noun={`your ${binderNoun}`}
                    catalog
                    onCatalogExit={refreshQuietly}
                    resetKey={activeTab}
                />
            )}
        </div>
    );
}

export default Binders;
